import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { apiFetch } from '../utils/apiFetch';
import { useParams, useNavigate } from 'react-router-dom';
import { notifySuccess, notifyError } from '../utils/toast';

interface Provider {
  id: number;
  name: string;
  is_provider: boolean;
}

const EventoProveedores: React.FC = () => 
{
  const { eventId, clientId } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const eventIdNum = Number(eventId);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setEventName] = useState<string>('');

  //-----------------------------------------------------------

  // Función para obtener proveedores
  const fetchProviders = useCallback(async () => 
  {
    setLoading(true);
    try 
    {
      const response = await apiFetch(`web/get_relation_providers_with_event?id_event=${eventId}&id_client=${clientId}`, {
        method: 'GET'
      });

      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers);
        setEventName(data.eventName);
      } else {
        navigate('/main/not-found'); 
      }
    } 
    catch (error) 
    {
      setError('Error al cargar proveedores.');
    } 
    finally 
    {
      setLoading(false);
    }
  }, [eventId, token]);

  //-----------------------------------------------------------

  // Cargar proveedores cuando cambie el eventId
  useEffect(() => 
  {
    fetchProviders();
  }, [fetchProviders]);

  //-----------------------------------------------------------

  // Manejar el cambio en los checkboxes
  const handleCheckboxChange = (id: number) => 
  {
    setProviders(prevProviders =>
      prevProviders.map(provider =>
        provider.id === id ? { ...provider, is_provider: !provider.is_provider } : provider
      )
    );
  };

  //-----------------------------------------------------------

  // Guardar los cambios
  const handleSave = async () => 
  {
    const selectedProviderIds = providers
      .filter(provider => provider.is_provider)
      .map(provider => provider.id)
      .join('-');

    try 
    {
      const response = await apiFetch(`web/update_event_providers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          id_event: eventIdNum.toString(),
          ids_providers: selectedProviderIds
        }).toString()
      });

      if (response.ok) 
      {
        navigate(`/main/recintos/${clientId}/eventos`);
        notifySuccess('Proveedores actualizados correctamente');
      } 
      else 
      {
        throw new Error('Error al guardar los cambios.');
      }
    } 
    catch (error) 
    {
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
      setError(errorMsg);
    }
  };

  //-----------------------------------------------------------

  return (
    <div className="eventos-container">
      <div className="miguitas">{title}</div>
      <div>
        <div className="cod-toolbar">
          <button className="icon-btn" onClick={() => navigate(`/main/recintos/${clientId}/eventos`)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M15 19l-7-7 7-7"/>
            </svg>
            Volver a Eventos
          </button>
        </div>
        {loading && <p>Buscando proveedores...</p>}
        {error && <p className="error-message">{error}</p>}
        {!loading && !error && providers.length === 0 && <p>No hay proveedores para mostrar.</p>}
        {!loading && !error && providers.length > 0 && (
          <table className="eventos-table">
            <tbody>
              {providers.map(provider => (
                <tr key={provider.id}>
                  <td className="evento-name">{provider.name}</td>
                  <td className="evento-autonext">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={provider.is_provider}
                        onChange={() => handleCheckboxChange(provider.id)}
                      />
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="pagination-controls">
        <div className="pagination-buttons"></div>
        <div className="pagination-info">
          <button onClick={handleSave}>Guardar</button>
        </div>
      </div>
    </div>
  );
};

export default EventoProveedores;
